/**
 * save-payload-helpers.ts
 *
 * Shared helpers for bounding the size of backtest/aggregation result payloads
 * before they are serialized and sent to the API save endpoint (POST /api/backtest/results/client).
 *
 * Problem (BUG-22): Wide/long aggregation results (21 subs × 2y × 1h) produce
 * per-bar equity series with ~17 000–370 000 points. JSON-serializing these
 * causes HTTP 413 Payload Too Large even at 50 MB body limits.
 *
 * Solution: downsample equity series to a configurable cap (default 3000 points)
 * using uniform-stride sampling that ALWAYS preserves the first and last points.
 * Trades and the correlations matrix are never downsampled.
 *
 * Shared by:
 *  - mcp-server/src/aggregate-save-helpers.ts  (buildAggregateSaveRecord)
 *  - mcp-server/src/api-client.ts              (saveBacktestResult)
 *  - mcp-server/src/job-runner.ts              (persistBacktest / persistAggregation)
 *  - src/web/api/client.ts                     (saveBacktestResult browser path)
 *
 * Rule 11: No silent fallbacks — throws on invalid cap.
 * Rule 12: No `as any` casts.
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Default maximum number of equity series points to persist. */
export const DEFAULT_EQUITY_CAP = 3000;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface EquityPoint {
  timestamp: number;
  equity: number;
  drawdown?: number;
}

export interface DownsampleResult<T extends EquityPoint> {
  /** The (potentially downsampled) series. */
  series: T[];
  /** True when the series was shortened; false when it fit within the cap. */
  downsampled: boolean;
  /** The original number of points before downsampling. */
  originalLength: number;
}

/**
 * A minimal shape for the save payload that boundSavePayload accepts.
 * Callers may pass records with additional fields — they are passed through.
 */
export interface SavePayloadInput {
  id: string;
  config: Record<string, unknown>;
  trades: unknown[];
  equity: EquityPoint[];
  metrics: Record<string, unknown>;
  perAssetResults?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface BoundedSavePayload extends SavePayloadInput {
  /** True when at least one equity series was downsampled. */
  downsampled: boolean;
  /** Original length of the portfolio equity series (before downsampling). */
  seriesOriginalLength: number;
}

// ---------------------------------------------------------------------------
// downsampleEquitySeries
// ---------------------------------------------------------------------------

/**
 * Downsample an equity series to at most `cap` points using uniform stride.
 *
 * Strategy:
 *  - If the series already fits within the cap → return as-is (no copy).
 *  - Otherwise sample `cap` evenly-spaced points using a float step of
 *    (length - 1) / (cap - 1) across the FULL index range [0, length-1],
 *    rounding each sample index to the nearest integer. This guarantees the
 *    output spans the entire original series (not just a prefix) and always
 *    includes index 0 and index (length-1) — the final point is appended
 *    explicitly (deduped if the last float-step sample already lands on it).
 *
 * Rule 11: throws when cap < 2 (a cap of 1 would drop either first or last).
 */
export function downsampleEquitySeries<T extends EquityPoint>(
  series: T[],
  cap: number,
): DownsampleResult<T> {
  if (cap < 2) {
    throw new Error(`downsampleEquitySeries: cap must be >= 2, got ${cap}`);
  }

  const originalLength = series.length;

  if (originalLength <= cap) {
    return { series, downsampled: false, originalLength };
  }

  // Uniform float-step sampling across the FULL series span. This yields
  // `cap` evenly-spaced points (including index 0 and index length-1) rather
  // than the old floor(length/cap) stride, which only ever covered a
  // contiguous prefix of the series before jumping straight to the last
  // point (SHA-62: dropped the middle/tail for any originalLength > cap).
  const out: T[] = [];
  const step = (originalLength - 1) / (cap - 1);
  for (let k = 0; k < cap - 1; k++) {
    out.push(series[Math.round(k * step)]);
  }

  // Always append the true last point, but avoid a duplicate if the final
  // sampled index above already landed on it.
  const lastPoint = series[originalLength - 1];
  if (out.length === 0 || out[out.length - 1].timestamp !== lastPoint.timestamp) {
    out.push(lastPoint);
  }

  return { series: out, downsampled: true, originalLength };
}

// ---------------------------------------------------------------------------
// downsampleEquityPreserveExtremes (SHA-84)
// ---------------------------------------------------------------------------

/**
 * Downsample an equity series to at most `cap` points, like
 * `downsampleEquitySeries`, but additionally GUARANTEES that the exact point
 * achieving the series' global max drawdown, global equity trough, and
 * global equity peak survive — plain uniform-stride sampling can (and does,
 * for real equity curves with a single sharp drawdown spike) skip straight
 * over the single worst bar, silently erasing it from the persisted chart.
 *
 * Used by mcp-server's computeAggregation (job-runner.ts, SHA-84) to slim
 * portfolio + per-asset equity curves IN COMPUTE, before the result crosses
 * the compute→supervisor socket frame — this is the actual OOM hot-spot for
 * 16-sub × ~4600-bar aggregations (the existing `downsampleEquitySeries` /
 * `boundSavePayload` only kick in later, right before the HTTP save, by
 * which point the full-size result has already been held in memory and
 * serialized across the socket at least once).
 *
 * Algorithm:
 *  1. If already within cap, return unchanged (no copy).
 *  2. Always keep index 0 and the last index.
 *  3. Then, budget permitting, also keep: the index of the global MAX
 *     drawdown value (if any point defines `drawdown`), the global MIN
 *     equity index (trough), and the global MAX equity index (peak) — added
 *     in that priority order and de-duplicated via a Set (ties/coincidences
 *     with the endpoints or each other cost nothing extra).
 *  4. Fill any remaining budget with uniform-stride sampling across the FULL
 *     index span (same full-range coverage guarantee as
 *     `downsampleEquitySeries` — see SHA-62).
 *  5. Return the selected points in ascending index (hence ascending
 *     timestamp) order.
 *
 * Rule 11: throws when cap < 2 (matches downsampleEquitySeries's contract —
 * a cap of < 2 cannot represent both a first and a last point).
 */
export function downsampleEquityPreserveExtremes<T extends EquityPoint>(
  series: T[],
  cap: number,
): DownsampleResult<T> {
  if (cap < 2) {
    throw new Error(`downsampleEquityPreserveExtremes: cap must be >= 2, got ${cap}`);
  }

  const originalLength = series.length;

  if (originalLength <= cap) {
    return { series, downsampled: false, originalLength };
  }

  const lastIndex = originalLength - 1;

  // Locate the global equity trough/peak and the global max-drawdown bar in
  // a single pass. maxDdIdx stays -1 when no point in the series defines a
  // `drawdown` field (e.g. per-asset curves built without it).
  let minEqIdx = 0;
  let maxEqIdx = 0;
  let maxDdIdx = -1;
  for (let i = 0; i < originalLength; i++) {
    if (series[i].equity < series[minEqIdx].equity) minEqIdx = i;
    if (series[i].equity > series[maxEqIdx].equity) maxEqIdx = i;
    const dd = series[i].drawdown;
    if (dd !== undefined && (maxDdIdx === -1 || dd > (series[maxDdIdx].drawdown as number))) {
      maxDdIdx = i;
    }
  }

  // Mandatory indices, in priority order: first, last, worst drawdown,
  // equity trough, equity peak. A Set de-duplicates for free when extremes
  // coincide with the endpoints or with each other.
  const mandatory = new Set<number>([0, lastIndex]);
  const niceToHave = [maxDdIdx, minEqIdx, maxEqIdx].filter((i) => i !== -1);
  for (const idx of niceToHave) {
    if (mandatory.size >= cap) break;
    mandatory.add(idx);
  }

  // Fill remaining budget with uniform-stride sampling across the FULL span
  // (0..lastIndex) so coverage never collapses to a prefix (SHA-62).
  const remaining = cap - mandatory.size;
  if (remaining > 0) {
    const step = lastIndex / (remaining + 1);
    for (let k = 1; k <= remaining; k++) {
      mandatory.add(Math.round(k * step));
    }
  }

  const sortedIndices = Array.from(mandatory).sort((a, b) => a - b);
  const out = sortedIndices.map((i) => series[i]);

  return { series: out, downsampled: true, originalLength };
}

// ---------------------------------------------------------------------------
// boundSavePayload
// ---------------------------------------------------------------------------

/**
 * Apply equity downsampling to a save payload (portfolio + per-asset equity).
 *
 * Returns a new object — never mutates the input.
 *
 * Fields affected:
 *  - payload.equity              → downsampled portfolio equity
 *  - payload.perAssetResults[*].equity → downsampled per-asset equity
 *
 * Fields NEVER modified:
 *  - payload.trades              (sparse, always kept)
 *  - perAssetResults[*].trades   (sparse, always kept)
 *  - perAssetResults[*].metrics  (compact, always kept)
 *  - config, metrics, id, etc.
 *
 * The returned object adds:
 *  - downsampled: boolean        — true when ≥ 1 series was shortened
 *  - seriesOriginalLength: number — original portfolio equity point count
 */
export function boundSavePayload(
  payload: SavePayloadInput,
  cap: number = DEFAULT_EQUITY_CAP,
): BoundedSavePayload {
  // Portfolio equity
  const portfolioResult = downsampleEquitySeries(payload.equity, cap);

  let anyDownsampled = portfolioResult.downsampled;

  // Per-asset equity
  let boundedPerAsset: Record<string, unknown> | undefined = payload.perAssetResults;
  if (payload.perAssetResults !== undefined) {
    const newPerAsset: Record<string, unknown> = {};
    for (const [symbol, par] of Object.entries(payload.perAssetResults)) {
      if (par === null || typeof par !== 'object') {
        // Pass through non-object values unchanged (Rule 11: no silent fallback
        // for valid data, but we must not crash if par is already null/primitive).
        newPerAsset[symbol] = par;
        continue;
      }
      const parObj = par as Record<string, unknown>;
      const parEquity = parObj['equity'];
      if (!Array.isArray(parEquity)) {
        // No equity field on this per-asset result → pass through as-is.
        newPerAsset[symbol] = par;
        continue;
      }
      const parEquityTyped = parEquity as EquityPoint[];
      const parResult = downsampleEquitySeries(parEquityTyped, cap);
      if (parResult.downsampled) {
        anyDownsampled = true;
      }
      newPerAsset[symbol] = { ...parObj, equity: parResult.series };
    }
    boundedPerAsset = newPerAsset;
  }

  return {
    ...payload,
    equity: portfolioResult.series,
    perAssetResults: boundedPerAsset,
    downsampled: anyDownsampled,
    seriesOriginalLength: portfolioResult.originalLength,
  };
}
